import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, ChevronRight, Sparkles,
  FileText, Droplets, Apple, Stethoscope, ClipboardList
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientHealthPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = `/${user?.user_type || 'client'}`;
  const [completedTests, setCompletedTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompletedTests();
  }, []);

  const fetchCompletedTests = async () => {
    try {
      const response = await axios.get(`${API}/health-tests/my-tests`);
      setCompletedTests(response.data);
    } catch (error) {
      console.error('Error fetching tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date();
  const hours = today.getHours();
  const greeting = hours < 12 ? 'Buenos días' : hours < 18 ? 'Buenas tardes' : 'Buenas noches';
  const dateStr = today.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-emerald-100 border-t-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="client-health-page">
      
      {/* Greeting Header */}
      <div className="pt-1">
        <p className="text-sm text-gray-400 uppercase tracking-widest font-medium capitalize">{dateStr}</p>
        <h1 className="text-4xl font-bold text-gray-900 mt-1.5" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {greeting}, <span className="bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">{user?.name?.split(' ')[0]}</span>
        </h1>
      </div>

      {/* Health Tests Hero Section */}
      <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100/50 overflow-hidden">
        <div className="relative overflow-hidden">
          <div className="p-8" style={{ background: 'linear-gradient(135deg, #3A601C, #467322, #5A8A2D)' }}>
            <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-sm" />
            <div className="absolute right-8 bottom-4 w-24 h-24 bg-white/5 rounded-full" />
            <div className="absolute left-1/2 -top-6 w-32 h-32 bg-white/10 rounded-full blur-xl" />
            
            <div className="relative z-10 flex items-center gap-5">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0 shadow-inner">
                <Stethoscope className="text-white" size={38} strokeWidth={1.5} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles size={14} className="text-amber-300" />
                  <span className="text-emerald-100 text-xs font-semibold uppercase tracking-wider">Evaluaciones de salud</span>
                </div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Tests de Salud
                </h2>
                <p className="text-white/70 mt-1 text-sm">
                  {completedTests.length > 0 
                    ? `Has completado ${completedTests.length} evaluaci${completedTests.length === 1 ? 'on' : 'ones'}`
                    : 'Realiza tu primera evaluacion de salud'}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6">
          <p className="text-gray-500 mb-5 text-sm leading-relaxed">
            Accede a evaluaciones especializadas de salud digestiva, metabolismo, neurotransmisores, estres y mas para obtener un panorama completo de tu bienestar.
          </p>
          <Button 
            onClick={() => navigate(`${basePath}/health-tests-list`)}
            className="w-full text-white py-5 text-base font-bold rounded-full transition-all duration-300 shadow-lg hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(90deg, #3A601C, #467322)', boxShadow: '0 10px 25px rgba(70,115,34,0.3)' }}
            data-testid="start-test-btn"
          >
            Ver Evaluaciones Disponibles
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </div>

      {/* Quick Access Grid - 5 cards */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5">
        <button
          onClick={() => navigate(`${basePath}/prescriptions`)}
          className="bg-white rounded-2xl border border-gray-100/50 p-3.5 text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group"
          data-testid="quick-access-recetas"
        >
          <div className="w-9 h-9 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform duration-300">
            <FileText size={18} className="text-blue-500" />
          </div>
          <p className="font-bold text-gray-900 text-xs" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Mis Recetas</p>
          <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">Recetas medicas</p>
          <div className="flex items-center gap-0.5 mt-2">
            <span className="text-[10px] font-semibold text-blue-500">Ver</span>
            <ChevronRight size={11} className="text-blue-400" />
          </div>
        </button>
        <button
          onClick={() => navigate(`${basePath}/analytics`)}
          className="bg-white rounded-2xl border border-gray-100/50 p-3.5 text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group"
          data-testid="quick-access-analiticas"
        >
          <div className="w-9 h-9 bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform duration-300">
            <Droplets size={18} className="text-violet-500" />
          </div>
          <p className="font-bold text-gray-900 text-xs" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Analiticas</p>
          <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">Resultados de sangre</p>
          <div className="flex items-center gap-0.5 mt-2">
            <span className="text-[10px] font-semibold text-violet-500">Ver</span>
            <ChevronRight size={11} className="text-violet-400" />
          </div>
        </button>
        <button
          onClick={() => navigate(`${basePath}/diet`)}
          className="bg-white rounded-2xl border border-gray-100/50 p-3.5 text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group"
          data-testid="health-card-mi-dieta"
        >
          <div className="w-9 h-9 bg-gradient-to-br from-lime-50 to-green-50 rounded-xl flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform duration-300">
            <Apple size={18} className="text-lime-600" />
          </div>
          <p className="font-bold text-gray-900 text-xs" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Mi Dieta</p>
          <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">Plan alimenticio</p>
          <div className="flex items-center gap-0.5 mt-2">
            <span className="text-[10px] font-semibold text-lime-600">Ver</span>
            <ChevronRight size={11} className="text-lime-500" />
          </div>
        </button>
        <button
          onClick={() => navigate(`${basePath}/health-tests-list`)}
          className="bg-white rounded-2xl border border-gray-100/50 p-3.5 text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group"
          data-testid="quick-access-tests"
        >
          <div className="w-9 h-9 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform duration-300">
            <Stethoscope size={18} className="text-emerald-600" />
          </div>
          <p className="font-bold text-gray-900 text-xs" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Test de Salud</p>
          <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">Evaluaciones</p>
          <div className="flex items-center gap-0.5 mt-2">
            <span className="text-[10px] font-semibold text-emerald-600">Ver</span>
            <ChevronRight size={11} className="text-emerald-500" />
          </div>
        </button>
        <button
          onClick={() => navigate(`${basePath}/health-tests/results`)}
          className="bg-white rounded-2xl border border-gray-100/50 p-3.5 text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group relative"
          data-testid="quick-access-historial"
        >
          {completedTests.length > 0 && (
            <span className="absolute top-2.5 right-2.5 min-w-[18px] h-[18px] bg-emerald-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1 shadow-sm">
              {completedTests.length}
            </span>
          )}
          <div className="w-9 h-9 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform duration-300">
            <ClipboardList size={18} className="text-amber-600" />
          </div>
          <p className="font-bold text-gray-900 text-xs" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Historial</p>
          <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{completedTests.length} test{completedTests.length !== 1 ? 's' : ''}</p>
          <div className="flex items-center gap-0.5 mt-2">
            <span className="text-[10px] font-semibold text-amber-600">Ver</span>
            <ChevronRight size={11} className="text-amber-500" />
          </div>
        </button>
      </div>
    </div>
  );
};

export default ClientHealthPage;
